import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, ClipboardCheck, Plus } from 'lucide-react';
import { OnboardingSteps } from '../components/firm/OnboardingSteps';
import { EmployeeDocAlerts } from '../components/firm/EmployeeDocAlerts';
import { KvkkRenewalBanner } from '../components/firm/KvkkRenewalBanner';
import { FirmStatusCards, ExpiringPermits } from '../components/firm/FirmPermitWidgets';
import { fetchAllPages } from '../hooks/useWorkPermits';
import { useScreenReady } from '../lib/perfScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="border-slate-200">
    <CardContent className="flex items-center gap-3 p-4 sm:gap-4 sm:p-6">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 sm:h-12 sm:w-12"><Icon className="h-5 w-5 text-slate-600 sm:h-6 sm:w-6" /></div>
      <div className="min-w-0"><p className="truncate text-xs text-slate-500 sm:text-sm">{label}</p><p className="text-2xl font-bold text-slate-900 sm:text-3xl" data-testid={testId}>{value}</p></div>
    </CardContent>
  </Card>
);

export default function FirmDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { myCompany, reloadMyCompany } = useOutletContext() || {};
  const locked = !!myCompany?.kvkk_renewal_required;
  const [counts, setCounts] = useState(null);
  useScreenReady('dashboard', counts !== null);
  const [permits, setPermits] = useState([]);
  const [locations, setLocations] = useState([]);
  const locationName = (id) => locations.find(l => l.id === id)?.name || '-';
  const openList = (state) => { localStorage.setItem('work-permits-view', 'list'); navigate('/work-permits', { state }); };

  useEffect(() => {
    fetchAllPages('/work-permits?fields=list').then(setPermits).catch(() => {});
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
    Promise.all([
      axios.get(`${API}/employees?page_size=500`),
      axios.get(`${API}/work-permits?page_size=1`),
    ]).then(([e, p]) => setCounts({
      employees: e.data.total,
      permits: p.data.total,
      hasDocs: (e.data.items || []).some(emp => Object.keys(emp.document_files || {}).length > 0),
    })).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="firm-dashboard">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Hoş geldiniz, {user?.name}</h1>
          <p className="mt-1 text-sm text-slate-500 sm:text-base">Firmanıza ait personel ve çalışma izni taleplerini buradan takip edebilirsiniz.</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:flex sm:shrink-0 sm:gap-3">
          <Button onClick={() => navigate('/work-permits/new')} disabled={locked} title={locked ? 'Yeni KVKK taahhütnamesini yükleyene kadar talep oluşturamazsınız' : undefined} className="w-full bg-slate-900 hover:bg-slate-800 sm:w-auto" data-testid="firm-dashboard-new-permit"><Plus className="h-4 w-4 mr-1" />Yeni Çalışma İzni Talebi</Button>
          <Button variant="outline" onClick={() => navigate('/employees')} className="w-full sm:w-auto" data-testid="firm-dashboard-employees-btn"><Users className="h-4 w-4 mr-1" />Personellerim</Button>
        </div>
      </div>
      <KvkkRenewalBanner company={myCompany} onChanged={reloadMyCompany} />
      {myCompany?.registration_status === 'approved' && counts && <OnboardingSteps counts={counts} />}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Personel" value={counts?.employees ?? 0} testId="firm-dashboard-employees" />
        <StatCard icon={ClipboardCheck} label="Çalışma İzni Talepleri" value={counts?.permits ?? 0} testId="firm-dashboard-permits" />
        <FirmStatusCards permits={permits} onOpen={(group) => openList({ dashGroup: group, dashPeriod: 'all' })} />
      </div>
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
        <ExpiringPermits permits={permits} locationName={locationName} onView={(p) => openList({ openPermitId: p.id })} />
        {counts?.employees > 0 && <EmployeeDocAlerts />}
      </div>
    </div>
  );
}
